import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { productsAPI } from '../../api/products';
import { useCart } from '../../contexts/CartContext';
import type { Product, ProductVariant } from '../../types';
import {
  ShoppingCart,
  Package,
  Truck,
  Shield,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { formatPrice } from '../../utils/utilityFunctions';
import { toast } from 'react-hot-toast';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem, isLoading: cartLoading } = useCart();

  const [product, setProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProduct(parseInt(id));
    }
  }, [id]);

  const fetchProduct = async (productId: number) => {
    setIsLoading(true);
    setError('');

    try {
      // Fetch product details
      const productResponse = await productsAPI.getProduct(productId);
      if (productResponse.data) {
        setProduct(productResponse.data);

        // Fetch variants
        const variantsResponse = await productsAPI.getProductVariants(productId);
        if (variantsResponse.data && variantsResponse.data.length > 0) {
          setVariants(variantsResponse.data);
          setSelectedVariant(variantsResponse.data[0]);
        }
      } else if (productResponse.error) {
        setError(productResponse.error);
      }
    } catch (err) {
      setError('Failed to load product details');
      console.error('Error fetching product:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product || !selectedVariant) return;

    setIsAddingToCart(true);
      const allImages = product.images?.map(img => img.image_url) || [];
console.log('Adding to cart:', product.vendor, product.vendor_name, selectedVariant, quantity, allImages);
    try {
      await addItem({
        product_variant_id: selectedVariant.id,
        product_id: product.id,
        product_title: product.title,
        variant_name: selectedVariant.name,
        sku: selectedVariant.full_sku || product.sku,
        price: product.selling_price,
        quantity,
        image_url: product.images?.[0]?.image_url,
        vendor_id: product.vendor || null,
        vendor_name: product.vendor_name || '',
        additional_images: allImages,
      });

      // Show modern toast notification
      toast.custom((t) => (
        <div className={`${t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <ShoppingCart className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Added to cart!
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {quantity} × {product.title} ({selectedVariant.name})
                </p>
                <div className="mt-2">
                  <button
                    onClick={() => {
                      toast.dismiss(t.id);
                      navigate('/cart');
                    }}
                    className="text-sm font-medium text-blue-600 hover:text-blue-500"
                  >
                    View cart →
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      ), {
        duration: 4000,
        position: 'bottom-right',
      });

    } catch (error: any) {
      toast.error(error.message || 'Failed to add to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    const maxQuantity = selectedVariant?.available_quantity || 0;

    if (newQuantity >= 1 && newQuantity <= maxQuantity) {
      setQuantity(newQuantity);
    }
  };

  const getStockStatus = () => {
    if (!selectedVariant || !selectedVariant.available_quantity) {
      return { text: 'Out of stock', color: 'text-danger-600', bg: 'bg-danger-100' };
    }

    if (selectedVariant.available_quantity <= 5) {
      return {
        text: `Only ${selectedVariant.available_quantity} left`,
        color: 'text-warning-600',
        bg: 'bg-warning-100'
      };
    }

    return {
      text: 'In stock',
      color: 'text-success-600',
      bg: 'bg-success-100'
    };
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-200 rounded-lg h-96"></div>
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {error || 'Product not found'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            The product you're looking for doesn't exist or is unavailable.
          </p>
          <div className="mt-6">
            <Link
              to="/products"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const stockStatus = getStockStatus();
  const images = product.images || [];
  const hasImages = images.length > 0;
  const isOutOfStock = !selectedVariant || (selectedVariant.available_quantity || 0) <= 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
      {/* Breadcrumb */}
      <nav className="mb-6">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/" className="hover:text-gray-700">Home</Link>
          </li>
          <li>
            <ChevronRight className="h-4 w-4" />
          </li>
          <li>
            <Link to="/products" className="hover:text-gray-700">Products</Link>
          </li>
          <li>
            <ChevronRight className="h-4 w-4" />
          </li>
          <li className="text-gray-900 font-medium truncate">
            {product.title}
          </li>
        </ol>
      </nav>

      {/* Product Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Images */}
        <div>
          {/* Main Image */}
          <div className="bg-white rounded-lg border overflow-hidden mb-4">
            <div className="aspect-w-4 aspect-h-3">
              {hasImages ? (
                <img
                  src={images[currentImageIndex].image_url}
                  alt={product.title}
                  className="w-full h-full object-contain p-4"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Package className="h-32 w-32 text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`aspect-square rounded border overflow-hidden ${currentImageIndex === index
                    ? 'ring-2 ring-primary-500 border-transparent'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <img
                    src={image.image_url}
                    alt={`${product.title} - ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <div className="sticky top-6">
            {/* Title & SKU */}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {product.title}
            </h1>
            <div className="text-sm text-gray-500 mb-4">
              SKU: {product.sku}
            </div>

            {/* Price */}
            <div className="mb-6">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {formatPrice(product.selling_price)}
              </div>
              {product.cost_price && product.cost_price > product.selling_price && (
                <div className="text-lg text-gray-500 line-through">
                  {formatPrice(product.cost_price)}
                </div>
              )}
            </div>

            {/* Stock Status */}
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-6 ${stockStatus.bg} ${stockStatus.color}`}>
              {stockStatus.text}
            </div>

            {/* Variants */}
            {variants.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Variant
                </label>
                <div className="flex flex-wrap gap-2">
                  {variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => {
                        setSelectedVariant(variant);
                        setQuantity(1); // Reset quantity when variant changes
                      }}
                      className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${selectedVariant?.id === variant.id
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        } ${variant.available_quantity <= 0
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                        }`}
                      disabled={variant.available_quantity <= 0}
                    >
                      {variant.name}
                      {variant.available_quantity <= 0 && ' (Out of stock)'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selector */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Quantity
              </label>
              <div className="flex items-center max-w-xs">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1 || isOutOfStock}
                  className="w-12 h-12 flex items-center justify-center border border-gray-300 rounded-l-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="text-xl font-light">−</span>
                </button>
                <div className="flex-1 h-12 flex items-center justify-center border-y border-gray-300">
                  <span className="text-xl font-semibold text-gray-900">{quantity}</span>
                </div>
                <button
                  onClick={() => handleQuantityChange(1)}
                  disabled={
                    isOutOfStock ||
                    quantity >= (selectedVariant?.available_quantity || 0)
                  }
                  className="w-12 h-12 flex items-center justify-center border border-gray-300 rounded-r-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="text-xl font-light">+</span>
                </button>
              </div>
              {selectedVariant && (
                <p className="mt-2 text-sm text-gray-500">
                  <span className="font-medium">{selectedVariant.available_quantity}</span> units available
                </p>
              )}
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              disabled={isOutOfStock || isAddingToCart || cartLoading}
              className={`w-full py-4 px-6 rounded-xl text-lg font-semibold flex items-center justify-center transition-all duration-200 mb-4 ${isOutOfStock
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                }`}
            >
              {isAddingToCart ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                  Adding to Cart...
                </>
              ) : (
                <>
                  <ShoppingCart className="mr-3 h-6 w-6" />
                  {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                </>
              )}
            </button>

            {/* Features */}
            <div className="space-y-3 border-t border-gray-200 pt-6">
              <div className="flex items-center text-sm text-gray-600">
                <Truck className="h-5 w-5 text-gray-400 mr-3" />
                <span>Free delivery in Port of Spain (orders over $100)</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Shield className="h-5 w-5 text-gray-400 mr-3" />
                <span>Cash on delivery available</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description & Details */}
      <div className="mt-12">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              className="border-b-2 border-blue-600 text-blue-600 py-4 px-1 text-sm font-medium"
            >
              Description
            </button>
            {product.specs && Object.keys(product.specs).length > 0 && (
              <button
                className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 py-4 px-1 text-sm font-medium"
              >
                Specifications
              </button>
            )}
          </nav>
        </div>

        <div className="py-8">
          {product.description ? (
            <div className="prose max-w-none">
              <p className="text-gray-700 whitespace-pre-line">
                {product.description}
              </p>
            </div>
          ) : (
            <p className="text-gray-500">No description available.</p>
          )}

          {product.specs && Object.keys(product.specs).length > 0 && (
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Specifications</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(product.specs).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">{key}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;